import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import Input from '../../components/form/input';
import SpectrumAnalyzer from './spectrumAnalyzer';
import FirebaseReceiver from '../../firebaseReceiver';
import { resolveRemotePin, localDateKey } from '../../lib/remotePin';
import { isNeiWindowOpen } from '../../lib/shotTrainerNeiWindow';

const LS_KEY = 'shottrainer.fieldCompact.settings';

function loadSettings() {
    try {
        const raw = localStorage.getItem(LS_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch (err) {
        return null;
    }
}

function saveSettings(partial) {
    try {
        const current = loadSettings() || {};
        const next = { ...current, ...partial };
        localStorage.setItem(LS_KEY, JSON.stringify(next));
    } catch (err) {
        // ignore quota / privacy errors
    }
}

@observer
class ShotTrainerFieldCompact extends Component {
    constructor(props) {
        super(props);
        const saved = loadSettings() || {};
        const resolvedPin = resolveRemotePin(saved, localDateKey());
        this.state = {
            firebaseReceiverSessionId: resolvedPin.pin,
        };
        this.remotePinIsNew = resolvedPin.isNew;
        this.firebaseReceiver = null;
    }

    setupReceiver = (sessionId) => {
        if (this.firebaseReceiver) {
            this.firebaseReceiver.tearDown();
            this.firebaseReceiver = null;
        }
        if (!sessionId) return;
        this.firebaseReceiver = new FirebaseReceiver(sessionId);
        this.firebaseReceiver.setCommand('getReadyForShooting', () => {
            const { shotTrainerStore } = this.props.stores;
            shotTrainerStore.getReadyForExercise();
        });
        this.firebaseReceiver.setCommand('startExercise', () => this.startExercise());
        this.firebaseReceiver.setCommand('stopExercise', () => this.stopExercise());
        this.firebaseReceiver.setCommand('emptyGunInspectEachOther', () => this.emptyGunInspectEachOther());
        this.firebaseReceiver.setCommand('nextStage', () => this.nextStage());
        this.firebaseReceiver.setCommand('inspectTargets', () => this.inspectTargets());
        this.firebaseReceiver.setCommand('notReady', () => this.notReady());
        this.firebaseReceiver.setCommand('finishExercise', () => this.finishExercise());
    }

    updateReceiverSessionId = (e) => {
        const { value } = e.target;
        this.setState({ firebaseReceiverSessionId: value });
        saveSettings({ firebaseReceiverSessionId: value, firebaseReceiverSessionIdDate: localDateKey() });
        this.setupReceiver(value);
    }

    componentDidMount() {
        const { shotTrainerStore } = this.props.stores;
        shotTrainerStore.initializeAudioContext();
        const saved = loadSettings();
        if (saved) {
            if (saved.shootingTime != null) {
                shotTrainerStore.updateObjectKeyValue('fieldExercise', 'shootingTime', saved.shootingTime);
            }
            if (saved.loadingTime != null) {
                shotTrainerStore.updateObjectKeyValue('fieldExercise', 'loadingTime', saved.loadingTime);
            }
            if (saved.norgesFelt != null) {
                shotTrainerStore.updateObjectKeyValue('fieldExercise', 'norgesFelt', saved.norgesFelt);
            }
        }
        // Persist the session id WITH today's date: reopens the same day keep the
        // phone pairing, but the first open on a new day rotates to a fresh PIN.
        if (this.remotePinIsNew) {
            saveSettings({
                firebaseReceiverSessionId: this.state.firebaseReceiverSessionId,
                firebaseReceiverSessionIdDate: localDateKey(),
            });
        }
        this.setupReceiver(this.state.firebaseReceiverSessionId);
    }

    componentWillUnmount() {
        const { shotTrainerStore } = this.props.stores;
        shotTrainerStore.stopSilentAudio();
        if (this.firebaseReceiver) {
            this.firebaseReceiver.tearDown();
            this.firebaseReceiver = null;
        }
    }

    handleFocus = (e) => {
        e.target.select();
    }

    updateExercise = (e) => {
        const { shotTrainerStore } = this.props.stores;
        const { name, value } = e.target;
        const numericValue = parseInt(value, 10);
        const stored = Number.isFinite(numericValue) ? numericValue : value;
        shotTrainerStore.updateObjectKeyValue('fieldExercise', name, stored);
        if (name === 'shootingTime' || name === 'loadingTime') {
            saveSettings({ [name]: stored });
        }
    }

    updateNorgesFelt = (e) => {
        const { shotTrainerStore } = this.props.stores;
        const checked = e.target.checked;
        shotTrainerStore.updateObjectKeyValue('fieldExercise', 'norgesFelt', checked);
        saveSettings({ norgesFelt: checked });
    }

    startExercise = () => {
        const { shotTrainerStore } = this.props.stores;
        shotTrainerStore.startExercise();
    }

    stopExercise = () => {
        const { shotTrainerStore } = this.props.stores;
        shotTrainerStore.stopExercise();
    }

    notReady = () => {
        const { shotTrainerStore } = this.props.stores;
        shotTrainerStore.notReady();
    }

    emptyGunInspectEachOther = () => {
        const { shotTrainerStore } = this.props.stores;
        shotTrainerStore.emptyGunInspectEachOther();
    }

    nextStage = () => {
        const { shotTrainerStore } = this.props.stores;
        shotTrainerStore.nextStage();
    }

    inspectTargets = () => {
        const { shotTrainerStore } = this.props.stores;
        shotTrainerStore.inspectTargets();
    }

    finishExercise = () => {
        const { shotTrainerStore } = this.props.stores;
        shotTrainerStore.finishExercise();
    }

    renderIdle() {
        const { shotTrainerStore } = this.props.stores;
        const { fieldExercise, commands } = shotTrainerStore;
        return (
            <div class='d-flex flex-column gap-2 px-3 py-2 h-100'>
                <div class='d-flex flex-row align-items-end gap-3'>
                    <div style='width: 110px;'>
                        <label class='form-label small text-body-secondary mb-1' for='shootingTime-compact'>Skytetid (s)</label>
                        <input
                            id='shootingTime-compact'
                            name='shootingTime'
                            type='tel'
                            pattern='[0-9]*'
                            inputmode='numeric'
                            class='form-control text-center'
                            style='font-size: 1.6rem; font-weight: 700; height: 48px;'
                            value={fieldExercise.shootingTime}
                            onInput={this.updateExercise}
                            onFocus={this.handleFocus}
                        />
                    </div>
                    <div style='width: 110px;'>
                        <label class='form-label small text-body-secondary mb-1' for='loadingTime-compact'>Ladetid (s)</label>
                        <input
                            id='loadingTime-compact'
                            name='loadingTime'
                            type='tel'
                            pattern='[0-9]*'
                            inputmode='numeric'
                            class='form-control text-center'
                            style='font-size: 1.6rem; font-weight: 700; height: 48px;'
                            value={fieldExercise.loadingTime}
                            onInput={this.updateExercise}
                            onFocus={this.handleFocus}
                        />
                    </div>
                    <div class='form-check ms-2 mb-2'>
                        <input type='checkbox' class='form-check-input' id='norgesfelt-compact' onInput={this.updateNorgesFelt} checked={fieldExercise.norgesFelt} />
                        <label class='form-check-label small' for='norgesfelt-compact'>Norgesfelt</label>
                    </div>
                    <div class='ms-auto' style='width: 110px;'>
                        <label class='form-label small text-body-secondary mb-1' for='remote-pin-compact'>
                            <i class='fa-duotone fa-mobile me-1' />Remote PIN
                        </label>
                        <input
                            id='remote-pin-compact'
                            name='firebaseReceiverSessionId'
                            type='tel'
                            pattern='[0-9]*'
                            inputmode='numeric'
                            class='form-control text-center'
                            style='font-size: 1.6rem; font-weight: 700; height: 48px;'
                            value={this.state.firebaseReceiverSessionId}
                            onInput={this.updateReceiverSessionId}
                            onFocus={this.handleFocus}
                        />
                    </div>
                </div>
                <div class='d-flex flex-row align-items-center gap-2'>
                    <button
                        disabled={commands.getReadyForShooting}
                        type='button'
                        class={`btn btn-sm btn-${commands.getReadyForShooting ? 'outline-' : ''}secondary rounded-pill`}
                        onClick={() => shotTrainerStore.prepareExercise()}
                    >
                        1. <Text id='shottrainer.get-ready-for-shooting'>Get ready for shooting</Text>
                        {commands.getReadyForShooting ? <i class='fa-duotone fa-check ms-1' /> : null}
                    </button>
                    <button
                        disabled={commands.loadAmmo}
                        type='button'
                        class={`btn btn-sm btn-${commands.loadAmmo ? 'outline-' : ''}secondary rounded-pill`}
                        onClick={() => shotTrainerStore.getReadyForExercise()}
                    >
                        2. <Text id='shottrainer.loadammo'>Prepare guns</Text>
                        {commands.loadAmmo ? <i class='fa-duotone fa-check ms-1' /> : null}
                    </button>
                    <button
                        disabled={commands.startExercise}
                        type='button'
                        class='btn btn-sm btn-primary rounded-pill ms-auto'
                        onClick={this.startExercise}
                    >
                        <i class='fa-duotone fa-play me-2' />
                        <Text id='shottrainer.start'>Start</Text>
                    </button>
                </div>
            </div>
        );
    }

    renderRunning() {
        const { shotTrainerStore } = this.props.stores;
        const { fieldExercise, loadingTime, readyTime, fireTime, shootingTime, shootingInProgress } = shotTrainerStore;
        let label = null;
        let value = 0;
        let total = 1;
        let phaseKey = 'idle';
        let valueClass = 'fw-bold';
        let progressColor = 'var(--bs-primary)';
        if (loadingTime > 0) {
            label = <Text id='shottrainer.field-loading'>Loading</Text>;
            value = loadingTime;
            total = fieldExercise.loadingTime || 1;
            phaseKey = 'loading';
            valueClass += ' text-primary';
            progressColor = 'var(--bs-primary)';
        } else if (readyTime > 0) {
            label = <Text id='shottrainer.field-shooter ready'>Shooter ready</Text>;
            value = readyTime;
            total = fieldExercise.readyTime || 1;
            phaseKey = 'ready';
            valueClass += ' text-body-secondary';
            progressColor = 'var(--bs-secondary)';
        } else if (fireTime > 0) {
            label = <Text id='shottrainer.field-ready'>Ready</Text>;
            value = fireTime;
            total = fieldExercise.fireTime || 1;
            phaseKey = 'fire';
            valueClass += ' text-warning';
            progressColor = 'var(--bs-warning)';
        } else if (shootingTime > 0) {
            if (shootingTime <= fieldExercise.stopTime) {
                label = <Text id='shottrainer.field-stop-shooting'>Haaaaaaaalt!</Text>;
                valueClass += ' text-danger';
                progressColor = 'var(--bs-danger)';
            } else {
                label = <Text id='shottrainer.field-shooting'>Shooting</Text>;
                valueClass += ' text-success';
                progressColor = 'var(--bs-success)';
            }
            phaseKey = 'shooting';
            value = shootingTime;
            total = fieldExercise.shootingTime || 1;
        }
        const radius = 42;
        const circumference = 2 * Math.PI * radius;
        return (
            <div class='d-flex flex-row align-items-center justify-content-between px-3 h-100'>
                <style>{`
                    @keyframes shottrainer-countdown {
                        from { stroke-dashoffset: 0; }
                        to { stroke-dashoffset: ${circumference}; }
                    }
                `}</style>
                <div class='d-flex align-items-center gap-3'>
                    <div style='position: relative; width: 130px; height: 130px;'>
                        <svg key={phaseKey} viewBox='0 0 100 100' style='width: 100%; height: 100%; transform: rotate(-90deg);'>
                            <circle
                                cx='50'
                                cy='50'
                                r={radius}
                                fill='none'
                                stroke='currentColor'
                                stroke-width='6'
                                style='opacity: 0.15;'
                            />
                            <circle
                                cx='50'
                                cy='50'
                                r={radius}
                                fill='none'
                                stroke={progressColor}
                                stroke-width='6'
                                stroke-linecap='round'
                                stroke-dasharray={circumference}
                                style={`animation: shottrainer-countdown ${total}s linear forwards;`}
                            />
                        </svg>
                        <div style='position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center;'>
                            <div class={valueClass} style='font-size: 3em; line-height: 1;'>{value}</div>
                            <small class='text-body-secondary' style='font-size: 0.75em; margin-top: 2px;'>{label}</small>
                        </div>
                    </div>
                </div>
                <div class='d-flex flex-column gap-2'>
                    {isNeiWindowOpen({ shootingInProgress, loadingTime, readyTime, fireTime }) ? (
                        <button
                            type='button'
                            class='btn btn-warning rounded-pill fw-bold text-nowrap'
                            style='font-size: 1.3rem;'
                            onClick={this.notReady}
                        >
                            <i class='fa-duotone fa-hourglass-half me-2' />
                            <Text id='shottrainer.notReady'>Not ready</Text>
                        </button>
                    ) : null}
                    <button
                        type='button'
                        class='btn btn-sm btn-danger rounded-pill'
                        onClick={this.stopExercise}
                    >
                        <i class='fa-duotone fa-stop me-2' />
                        <Text id='shottrainer.stop'>Stop</Text>
                    </button>
                </div>
            </div>
        );
    }

    renderDone() {
        const { shotTrainerStore } = this.props.stores;
        const { fieldExercise, commands } = shotTrainerStore;
        return (
            <div class='d-flex flex-row align-items-center gap-2 flex-wrap px-3 py-2 h-100'>
                <i class='fa-solid fa-hand text-danger' style='font-size: 2.5em;' />
                <small class='text-body-secondary me-2'>
                    <Text id='shottrainer.field-done'>Done</Text>
                </small>
                {fieldExercise.norgesFelt ? (
                    <>
                        <button
                            disabled={commands.emptyGunCheckEachOther}
                            type='button'
                            class={`btn btn-sm btn-${commands.emptyGunCheckEachOther ? 'outline-' : ''}primary rounded-pill`}
                            onClick={this.emptyGunInspectEachOther}
                        >
                            1. <Text id='shottrainer.empty-gun-inspect-each-other'>Empty gun and inspect</Text>
                            {commands.emptyGunCheckEachOther ? <i class='fa-duotone fa-check ms-1' /> : null}
                        </button>
                        <button
                            disabled={commands.nextStage}
                            type='button'
                            class={`btn btn-sm btn-${commands.nextStage ? 'outline-' : ''}primary rounded-pill`}
                            onClick={() => shotTrainerStore.nextStage()}
                        >
                            2. <Text id='shottrainer.secure-next-stage'>Next stage</Text>
                            {commands.nextStage ? <i class='fa-duotone fa-check ms-1' /> : null}
                        </button>
                    </>
                ) : (
                    <>
                        <button
                            disabled={commands.emptyGun}
                            type='button'
                            class={`btn btn-sm btn-${commands.emptyGun ? 'outline-' : ''}primary rounded-pill`}
                            onClick={() => shotTrainerStore.emptyGun()}
                        >
                            1. <Text id='shottrainer.empty-gun'>Empty gun</Text>
                            {commands.emptyGun ? <i class='fa-duotone fa-check ms-1' /> : null}
                        </button>
                        <button
                            disabled={commands.secureGun}
                            type='button'
                            class={`btn btn-sm btn-${commands.secureGun ? 'outline-' : ''}primary rounded-pill`}
                            onClick={() => shotTrainerStore.secureGun()}
                        >
                            2. <Text id='shottrainer.secure-gun'>Secure gun</Text>
                            {commands.secureGun ? <i class='fa-duotone fa-check ms-1' /> : null}
                        </button>
                    </>
                )}
                <button
                    disabled={commands.inspectTargets}
                    type='button'
                    class={`btn btn-sm btn-${commands.inspectTargets ? 'outline-' : ''}primary rounded-pill`}
                    onClick={() => shotTrainerStore.inspectTargets()}
                >
                    3. <Text id='shottrainer.inspect-targets'>Inspect targets</Text>
                    {commands.inspectTargets ? <i class='fa-duotone fa-check ms-1' /> : null}
                </button>
                <button
                    type='button'
                    class='btn btn-sm btn-danger rounded-pill ms-auto'
                    onClick={this.finishExercise}
                >
                    <i class='fa-duotone fa-stop me-2' />
                    <Text id='shottrainer.finish'>Finish</Text>
                </button>
            </div>
        );
    }

    render() {
        const { shotTrainerStore, userStore } = this.props.stores;
        const { shootingInProgress, shootingDone } = shotTrainerStore;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');
        const analyzerColor = darkmode ? 'rgba(255, 255, 255, 0.85)' : 'var(--bs-primary)';

        let inner;
        if (shootingInProgress) {
            inner = shootingDone ? this.renderDone() : this.renderRunning();
        } else {
            inner = this.renderIdle();
        }
        return (
            <div class='d-flex flex-column h-100'>
                <div class='flex-grow-1'>
                    {inner}
                </div>
                <div class='d-flex justify-content-center' style='height: 28px; pointer-events: none;'>
                    <SpectrumAnalyzer stores={this.props.stores} width={200} height={28} color={analyzerColor} />
                </div>
            </div>
        );
    }
}

export default ShotTrainerFieldCompact;
